/**
 * KennelAvatar — deterministyczny avatar z inicjałami i gradientem.
 *
 * Użycie: fallback gdy karta katalogu nie ma zdjęcia.
 * Inicjały: pierwsze litery 2 pierwszych słów nazwy (pomijając prefiksy typu "Hodowla"/"FCI").
 * Gradient: 1 z 8 presetów wybrany deterministycznie na bazie hasha slug/nazwy.
 */

const GRADIENTS = [
  'from-[#F47A3B] via-[#E06A2E] to-[#C95A24]', // orange
  'from-[#3B82F6] via-[#2563EB] to-[#1E40AF]', // blue
  'from-[#10B981] via-[#059669] to-[#047857]', // emerald
  'from-[#8B5CF6] via-[#7C3AED] to-[#6D28D9]', // violet
  'from-[#EC4899] via-[#DB2777] to-[#BE185D]', // pink
  'from-[#F59E0B] via-[#D97706] to-[#B45309]', // amber
  'from-[#0EA5E9] via-[#0284C7] to-[#0369A1]', // sky
  'from-[#14B8A6] via-[#0D9488] to-[#0F766E]', // teal
];

const SKIP_WORDS = new Set([
  'hodowla', 'hodowli', 'hodowlę', 'fci', 'zkwp', 'kennel', 'psów', 'psow', 'the', 'of', 'and', 'z', 'i', 'oraz'
]);

function getInitials(name) {
  if (!name) return '??';
  const words = name
    .trim()
    .split(/\s+/)
    .filter(w => !SKIP_WORDS.has(w.toLowerCase()) && /[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż]/.test(w));
  const pickable = words.length > 0 ? words : name.trim().split(/\s+/);
  const letters = pickable.slice(0, 2).map(w => w[0]?.toUpperCase() || '').filter(Boolean);
  return letters.length > 0 ? letters.join('') : name.substring(0, 2).toUpperCase();
}

function hashString(str) {
  let h = 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

/**
 * Props:
 *  - name: string — nazwa hodowli / tytuł ogłoszenia (dla inicjałów)
 *  - seed: string — slug lub id (dla gradientu, stały pomiędzy renderami)
 *  - type: 'hodowla' | 'miot' | 'reproduktor' — wpływa na overlay copy
 *  - className: extra classes (rozmiar ustalany przez parent)
 */
export default function KennelAvatar({ name, seed, type = 'hodowla', className = '' }) {
  const initials = getInitials(name);
  const gradientIdx = hashString(seed || name) % GRADIENTS.length;
  const gradient = GRADIENTS[gradientIdx];

  const overlayText = type === 'miot'
    ? 'Zdjęcia szczeniąt wkrótce'
    : type === 'reproduktor'
      ? 'Zdjęcia wkrótce'
      : 'Zdjęcia wkrótce';

  return (
    <div
      className={`relative w-full h-full flex flex-col items-center justify-center bg-gradient-to-br ${gradient} ${className}`}
      data-testid="kennel-avatar"
    >
      {/* subtle noise overlay dla głębi */}
      <div
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.3) 0%, transparent 40%)',
        }}
      />
      <span
        className="relative text-white font-black tracking-tight drop-shadow-sm"
        style={{ fontSize: 'clamp(22px, 3.2vw, 34px)', letterSpacing: '-0.02em' }}
      >
        {initials}
      </span>
      <span className="relative text-[9px] text-white/65 font-medium uppercase tracking-widest mt-1">
        {overlayText}
      </span>
    </div>
  );
}
